/**
 * PagarCobro — la pagina PUBLICA del link de cobro (/pagar/:linkId).
 * El cliente de la empresa NO necesita FlowexPay: ve quien le cobra, cuanto y por que,
 * y paga con PSE desde su banco (el banco lo autentica; nosotros jamas pedimos claves).
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = axios.create({ baseURL: import.meta.env.VITE_INVESTMENTS_API_URL || 'http://localhost:5001' });
const cop = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-CO');

export default function PagarCobro() {
  const { linkId } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [pagando, setPagando] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    API.get(`/api/pagar/${linkId}`)
      .then((r) => setInfo(r.data?.data?.cobro || null))
      .catch(() => setError('Este link de cobro no existe o ya no esta disponible.'));
  }, [linkId]);

  const pagar = async () => {
    setPagando(true); setMsg(null);
    try {
      const r = await API.post(`/api/pagar/${linkId}/pse`);
      const d = r.data?.data;
      if (d?.ok && d.checkout) {
        window.location.href = `https://${d.checkout}`; // al portal PSE del banco
      } else {
        setMsg(d?.mensaje || 'No se pudo iniciar el pago. Intenta de nuevo.');
        setPagando(false);
      }
    } catch {
      setMsg('No se pudo iniciar el pago. Intenta de nuevo.');
      setPagando(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-5">
          <span className="text-xl font-extrabold text-[#092F7B]">Flowex<span className="text-[#12B76A]">Pay</span></span>
          <p className="text-[11px] text-[#6B7280] mt-0.5">Pago seguro con PSE · nunca te pedimos tus claves</p>
        </div>

        {error && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 text-center text-sm text-[#6B7280]">{error}</div>
        )}

        {!error && !info && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 text-center text-sm text-[#9CA3AF]">Cargando el cobro…</div>
        )}

        {info && (
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6">
            <p className="text-xs text-[#6B7280] mb-1">Cuenta de cobro de</p>
            <p className="text-lg font-extrabold text-[#181D27]">{info.empresa}</p>
            {info.nit && <p className="text-[11px] text-[#9CA3AF]">NIT {info.nit}</p>}

            <div className="border-t border-[#F5F5F5] my-4" />

            <div className="flex justify-between items-start mb-2 text-sm">
              <span className="text-[#6B7280]">Concepto</span>
              <span className="font-medium text-[#181D27] text-right max-w-[220px]">{info.concepto}</span>
            </div>
            {info.vence && (
              <div className="flex justify-between items-center mb-2 text-sm">
                <span className="text-[#6B7280]">Vence</span>
                <span className="text-[#181D27]">{new Date(info.vence).toLocaleDateString('es-CO', { day: '2-digit', month: 'long' })}</span>
              </div>
            )}
            <div className="flex justify-between items-center mt-4 mb-5">
              <span className="text-sm text-[#6B7280]">Total a pagar</span>
              <span className="text-2xl font-extrabold text-[#181D27]">{cop(info.montoCop)} <span className="text-xs font-semibold text-[#6B7280]">COP</span></span>
            </div>

            {info.estado === 'pagado' && (
              <div className="rounded-xl bg-[#ECFDF3] border border-[#A6F4C5] text-[#047857] text-sm font-semibold text-center py-3">✓ Este cobro ya fue pagado. ¡Gracias!</div>
            )}
            {info.estado === 'anulado' && (
              <div className="rounded-xl bg-[#F2F4F7] text-[#475467] text-sm text-center py-3">Este cobro fue anulado por la empresa.</div>
            )}
            {(info.estado === 'pendiente' || info.estado === 'vencido') && (
              !info.habilitado ? (
                <div className="rounded-xl bg-[#FFFAEB] border border-[#F4D68C] text-[#B54708] text-sm text-center py-3">Los pagos por link están en habilitación. Intenta más tarde.</div>
              ) : (
                <>
                  <button onClick={pagar} disabled={pagando}
                    className="w-full py-3 rounded-xl bg-[#092F7B] text-white font-bold text-sm disabled:opacity-60">
                    {pagando ? 'Conectando con tu banco…' : 'Pagar con PSE'}
                  </button>
                  {msg && <p className="text-xs text-[#B42318] text-center mt-2">{msg}</p>}
                  <p className="text-[10px] text-[#9CA3AF] text-center mt-3">Serás dirigido al portal seguro de tu banco para autorizar el pago. FlowexPay nunca ve tus claves.</p>
                </>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
